import type { paths } from '$api-clients/generated/core';

type GetReferralOperation = paths['/referrals/{referralId}']['get'];
type GetReferralsOperation = paths['/referrals']['get'];
type PostReferralOperation = paths['/referrals']['post'];

// Client type enum
export enum ClientType {
  Buyer = 'Buyer',
  Seller = 'Seller',
  Landlord = 'Landlord',
  Tenant = 'Tenant',
}

// Client types object structure extracted from Core API schema
export type ClientTypes = NonNullable<ReferralClientRaw['clientTypes']>;

// Extract referral type from Core API schema
export type ReferralTypeValue = NonNullable<ReferralRaw['referralType']>;

// Extract agreement status type from Core API endpoint response
type AgreementStatusFromSchema = NonNullable<
  ReferralSummaryRaw['agreementStatus']
>;

// Agreement status values extracted from Core API schema
export const AgreementStatus = {
  PartiallySigned: 'partiallySigned',
  NotFinalized: 'notFinalized',
  Completed: 'completed',
  Created: 'created',
  Declined: 'declined',
  Delivered: 'delivered',
  Failed: 'failed',
  Sending: 'sending',
  Sent: 'sent',
  Voided: 'voided',
} as const satisfies Record<string, AgreementStatusFromSchema>;

export type AgreementStatus = AgreementStatusFromSchema;

// Special transaction status constants
export const TransactionStatusMissing = 'missing';
export const TransactionStatusExternal = 'external';

// Extract offer status from Core API schema
type OfferStatus = NonNullable<
  paths['/offers/{offerId}']['get']['responses']['200']['content']['application/json']['status']
>;

// Extract listing status from Core API schema
type ListingStatus = NonNullable<
  paths['/listings/{listingId}']['get']['responses']['200']['content']['application/json']['status']
>;

// Interaction status combines all API statuses plus UI-only statuses
export type InteractionStatus =
  | OfferStatus
  | ListingStatus
  | typeof TransactionStatusMissing
  | typeof TransactionStatusExternal;

type ReferralRaw =
  GetReferralOperation['responses']['200']['content']['application/json'];

export type ReferralSummaryRaw = NonNullable<
  GetReferralsOperation['responses']['200']['content']['application/json']['results']
>[number];

export type ReferralPostBody = NonNullable<
  PostReferralOperation['requestBody']
>['content']['application/json'];

// Raw API types (no derived fields)
export type ReferralAgentRaw = NonNullable<ReferralRaw['referringAgent']>;
export type ReferralAgentSummaryRaw = NonNullable<
  ReferralSummaryRaw['referringAgent']
>;
export type ReferralClientRaw = NonNullable<ReferralRaw['client']>;
export type ReferralClientSummaryRaw = NonNullable<
  ReferralSummaryRaw['client']
>;

// Derived field types
type DerivedAgentFields = {
  displayName: string;
  isSideAgent: boolean;
  companyName: string | null;
};

type DerivedClientFields = {
  displayName: string;
  clientTypesList: ClientType[];
};

// Augmented types (with derived fields) - returned by hooks
export type ReferralAgent = ReferralAgentRaw & DerivedAgentFields;
export type ReferralAgentSummary = ReferralAgentSummaryRaw & DerivedAgentFields;
export type ReferralClient = ReferralClientRaw & DerivedClientFields;
export type ReferralClientSummary = ReferralClientSummaryRaw &
  DerivedClientFields;

export type Referral = Omit<
  ReferralRaw,
  'referringAgent' | 'transactingAgent' | 'client'
> & {
  referringAgent?: ReferralAgent;
  transactingAgent?: ReferralAgent;
  client?: ReferralClient;
  transactionStatus: InteractionStatus;
};

export type ReferralSummary = Omit<
  ReferralSummaryRaw,
  'referringAgent' | 'transactingAgent' | 'client' | 'agreementStatus'
> & {
  referringAgent?: ReferralAgentSummary;
  transactingAgent?: ReferralAgentSummary;
  client?: ReferralClientSummary;
  agreementStatus: AgreementStatus;
  transactionStatus: InteractionStatus;
};
